import {
  ReactNode,
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  Dispatch,
  SetStateAction,
} from "react";
import * as L from "leaflet";
import { LIcon } from "../Components/LIcon";
import {
  DRAWINGS_PANE_NAME,
  DRAWING_HIT_STROKE_CLASS_NAME,
} from "../utils/mapRegistry";
import { uninstallEdgeMeasureLabels } from "../utils/edgeMeasureLabels";

// Constants
const LAYER_COLOR = "#0C2856";
const LAYER_WEIGHT = 4;
const LAYER_OPACITY = 1;
const CIRCLE_MARKER_RADIUS = 3;
const DEFAULT_DISTANCE_UNIT = "km";
const DEFAULT_AREA_UNIT = "m²";
const LOCAL_STORAGE_KEY = "atlas-drawings";

// Types
export type DrawingType =
  | "LOCATION"
  | "MEASURE"
  | "MEASURE_AREA"
  | "CITY"
  | "DRAW"
  | "UPLOAD"
  | "PROFILE";

export interface ProfilePoint {
  distance: number; // km from line start
  value: number | null; // raster pixel value (null = nodata)
}

export type ModalFunction = DrawingType | "PRINT" | null;

export const MAP_TOOLS = {
  LOCATION: "LOCATION",
  MEASURE: "MEASURE",
  MEASURE_AREA: "MEASURE_AREA",
  CITY: "CITY",
  DRAW: "DRAW",
  PRINT: "PRINT",
  UPLOAD: "UPLOAD",
  PROFILE: "PROFILE",
} as const;

export const MAP_TOOL_OPTIONS: ModalFunction[] = [
  ...Object.values(MAP_TOOLS),
  null,
];

export const MAP_UNITS = {
  KM: "km",
  M2: "m²",
  KM2: "km²",
  HECTARE: "ha",
} as const;

export interface Drawing {
  id: string;
  name: string;
  type: DrawingType;
  result: string | null;
  rawValue?: number;
  unit?: string;
  layer: L.Layer;
  isVisible: boolean;
}

interface SerializedDrawing {
  id: string;
  name: string;
  type: DrawingType;
  result: string | null;
  rawValue?: number;
  unit?: string;
  isVisible: boolean;
  layerData: {
    type: "marker" | "circleMarker" | "polyline" | "polygon";
    coordinates: number[][] | number[][][];
    latlng?: { lat: number; lng: number };
  };
}

interface IRulerModalContext {
  isModalOpen: boolean;
  activeFunction: ModalFunction;
  measureResult: string | null;
  isFinished: boolean;
  isConfirmed: boolean;
  currentLayer: L.Layer | null;
  drawings: Drawing[];
  setDrawings: Dispatch<SetStateAction<Drawing[]>>;

  openModal: () => void;
  closeModal: () => void;
  activeFunctionHandler: (func: ModalFunction) => void;

  setMeasure: (
    value: string | null,
    layer: L.Layer,
    rawValue?: number,
    unit?: string
  ) => void;
  cancelMeasure: () => void;
  confirmMeasure: () => void;
  removeDrawing: (id: string) => void;
  toggleDrawingVisibility: (id: string) => void;
  updateDrawingName: (id: string, name: string) => void;
  addLayersToMap: (featureGroup: L.FeatureGroup, mapInstance?: L.Map) => void;
  clearAllDrawings: () => void;
  downloadGeoJSON: () => void;
  profileData: ProfilePoint[] | null;
  isLoadingProfile: boolean;
  profileLayerName: string | null;
  profileLayerRef: React.MutableRefObject<L.Layer | null>;
  profileGenRef: React.MutableRefObject<number>;
  setProfileData: (data: ProfilePoint[] | null) => void;
  setIsLoadingProfile: (loading: boolean) => void;
  setProfileLayerName: (name: string | null) => void;
  clearProfile: () => void;
}

export const RulerModalContext = createContext<IRulerModalContext | undefined>(
  undefined
);

interface IRulerModalProvider {
  children: ReactNode;
}

export const RulerModalProvider = ({ children }: IRulerModalProvider) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [activeFunction, setActiveFunction] = useState<ModalFunction>(null);
  const [measureResult, setMeasureResult] = useState<string | null>(null);
  const [isFinished, setIsFinished] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [currentLayer, setCurrentLayer] = useState<L.Layer | null>(null);
  const [drawings, setDrawings] = useState<Drawing[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const featureGroupRef = useRef<L.FeatureGroup | null>(null);
  const [profileData, setProfileData] = useState<ProfilePoint[] | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [profileLayerName, setProfileLayerName] = useState<string | null>(null);
  const profileLayerRef = useRef<L.Layer | null>(null);
  const profileGenRef = useRef(0);

  const clearProfile = useCallback(() => {
    profileGenRef.current++; // invalidate any in-flight fetch
    if (profileLayerRef.current) {
      uninstallEdgeMeasureLabels(profileLayerRef.current as L.Polyline);
      try { profileLayerRef.current.remove(); } catch {}
      profileLayerRef.current = null;
    }
    setProfileData(null);
    setIsLoadingProfile(false);
    setProfileLayerName(null);
  }, []);

  const serializeDrawing = (drawing: Drawing): SerializedDrawing | null => {
    try {
      const layer = drawing.layer as any;
      let layerData: SerializedDrawing["layerData"];

      if (layer instanceof L.Marker) {
        const latlng = layer.getLatLng();
        layerData = {
          type: "marker",
          coordinates: [],
          latlng: { lat: latlng.lat, lng: latlng.lng },
        };
      } else if (layer instanceof L.CircleMarker) {
        const latlng = layer.getLatLng();
        layerData = {
          type: "circleMarker",
          coordinates: [],
          latlng: { lat: latlng.lat, lng: latlng.lng },
        };
      } else if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
        const latlngs = layer.getLatLngs() as L.LatLng[];
        layerData = {
          type: "polyline",
          coordinates: latlngs.map((ll) => [ll.lat, ll.lng]),
        };
      } else if (layer instanceof L.Polygon) {
        const latlngs = layer.getLatLngs()[0] as L.LatLng[];
        layerData = {
          type: "polygon",
          coordinates: [latlngs.map((ll) => [ll.lat, ll.lng])],
        };
      } else {
        return null;
      }

      return {
        id: drawing.id,
        name: drawing.name,
        type: drawing.type,
        result: drawing.result,
        rawValue: drawing.rawValue,
        unit: drawing.unit,
        isVisible: drawing.isVisible,
        layerData,
      };
    } catch (error) {
      console.error("Error serializing drawing:", error);
      return null;
    }
  };

  const deserializeDrawing = (
    serialized: SerializedDrawing
  ): Drawing | null => {
    try {
      let layer: L.Layer;

      if (
        serialized.layerData.type === "marker" &&
        serialized.layerData.latlng
      ) {
        layer = L.marker(
          [serialized.layerData.latlng.lat, serialized.layerData.latlng.lng],
          { icon: LIcon }
        );
      } else if (
        serialized.layerData.type === "circleMarker" &&
        serialized.layerData.latlng
      ) {
        layer = L.circleMarker(
          [serialized.layerData.latlng.lat, serialized.layerData.latlng.lng],
          {
            radius: CIRCLE_MARKER_RADIUS,
            color: LAYER_COLOR,
            fillColor: LAYER_COLOR,
            fillOpacity: 1,
            weight: 0,
            pane: DRAWINGS_PANE_NAME,
          }
        );
      } else if (serialized.layerData.type === "polyline") {
        const coords = serialized.layerData.coordinates as number[][];
        layer = L.polyline(
          coords.map(([lat, lng]) => [lat, lng]),
          {
            color: LAYER_COLOR,
            weight: LAYER_WEIGHT,
            opacity: LAYER_OPACITY,
            pane: DRAWINGS_PANE_NAME,
            className: DRAWING_HIT_STROKE_CLASS_NAME,
          }
        );
      } else if (serialized.layerData.type === "polygon") {
        const coords = serialized.layerData.coordinates as number[][][];
        layer = L.polygon(
          coords[0].map(([lat, lng]) => [lat, lng]),
          {
            color: LAYER_COLOR,
            weight: LAYER_WEIGHT,
            opacity: LAYER_OPACITY,
            pane: DRAWINGS_PANE_NAME,
            className: DRAWING_HIT_STROKE_CLASS_NAME,
          }
        );
      } else {
        return null;
      }

      return {
        id: serialized.id,
        name: serialized.name,
        type: serialized.type,
        result: serialized.result,
        rawValue: serialized.rawValue,
        unit: serialized.unit,
        layer,
        isVisible: serialized.isVisible,
      };
    } catch (error) {
      console.error("Error deserializing drawing:", error);
      return null;
    }
  };

  const saveToLocalStorage = useCallback((drawingsToSave: Drawing[]) => {
    try {
      const serialized = drawingsToSave
        .map(serializeDrawing)
        .filter((d): d is SerializedDrawing => d !== null);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(serialized));
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  }, []);

  const loadFromLocalStorage = useCallback((): Drawing[] => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!saved) return [];

      const serialized: SerializedDrawing[] = JSON.parse(saved);
      return serialized
        .map(deserializeDrawing)
        .filter((d): d is Drawing => d !== null);
    } catch (error) {
      console.error("Error loading from localStorage:", error);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!isInitialized) {
      const loadedDrawings = loadFromLocalStorage();
      if (loadedDrawings.length > 0) {
        setDrawings(loadedDrawings);
      }
      setIsInitialized(true);
    }
  }, [isInitialized, loadFromLocalStorage]);

  useEffect(() => {
    if (isInitialized) {
      saveToLocalStorage(drawings);
    }
  }, [drawings, isInitialized, saveToLocalStorage]);

  const getDefaultDrawingName = useCallback(
    (type: DrawingType): string => {
      const typeCount = drawings.filter((d) => d.type === type).length + 1;

      const nameTemplates: Record<string, string> = {
        [MAP_TOOLS.LOCATION]: `Marcador ${typeCount}`,
        [MAP_TOOLS.MEASURE]: `Distância ${typeCount}`,
        [MAP_TOOLS.MEASURE_AREA]: `Área ${typeCount}`,
        [MAP_TOOLS.DRAW]: `Desenho ${typeCount}`,
      };

      return nameTemplates[type] || `Desenho ${typeCount}`;
    },
    [drawings]
  );

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const clearAllDrawings = useCallback(() => {
    if (currentLayer) {
      try {
        currentLayer.remove();
      } catch {
        /* already removed */
      }
    }
    setCurrentLayer(null);
    setIsFinished(false);
    setIsConfirmed(false);
    setMeasureResult(null);
  }, [currentLayer]);

  const activeFunctionHandler = useCallback(
    (func: ModalFunction) => {
      if (activeFunction === func) {
        return;
      }

      // Clean up any unconfirmed layer before switching tools
      if (currentLayer && !isConfirmed) {
        currentLayer.remove();
      }

      setActiveFunction(func);

      if (func !== MAP_TOOLS.PRINT) {
        setIsFinished(false);
        setIsConfirmed(false);
        setCurrentLayer(null);
        setMeasureResult(null);
      }
    },
    [activeFunction, currentLayer, isConfirmed]
  );

  const setMeasure = useCallback(
    (
      value: string | null,
      layer: L.Layer,
      rawValue?: number,
      unit?: string
    ) => {
      setMeasureResult(value);
      setCurrentLayer(layer);
      setIsFinished(true);
      setIsConfirmed(false);

      (layer as any)._rawValue = rawValue;
      (layer as any)._unit = unit;
    },
    []
  );

  const cancelMeasure = useCallback(() => {
    if (currentLayer) {
      currentLayer.remove();
    }
    setMeasureResult(null);
    setCurrentLayer(null);
    setIsFinished(false);
    setIsConfirmed(false);
    setActiveFunction(null);
  }, [currentLayer]);

  const confirmMeasure = useCallback(() => {
    if (isConfirmed) {
      cancelMeasure();
      return;
    }

    if (currentLayer && activeFunction !== MAP_TOOLS.PRINT) {
      const drawingType = activeFunction as DrawingType;
      const defaultName = getDefaultDrawingName(drawingType);
      const defaultUnit =
        drawingType === MAP_TOOLS.MEASURE
          ? DEFAULT_DISTANCE_UNIT
          : DEFAULT_AREA_UNIT;

      const newDrawing: Drawing = {
        id: `drawing-${Date.now()}-${Math.random()}`,
        name: defaultName,
        type: drawingType,
        result: measureResult,
        rawValue: (currentLayer as any)._rawValue,
        unit: (currentLayer as any)._unit || defaultUnit,
        layer: currentLayer,
        isVisible: true,
      };
      setDrawings((prev) => [...prev, newDrawing]);
    }

    setIsConfirmed(true);
    setMeasureResult(null);
    setCurrentLayer(null);
    setIsFinished(false);
    setActiveFunction(null);
  }, [
    isConfirmed,
    measureResult,
    currentLayer,
    activeFunction,
    cancelMeasure,
    getDefaultDrawingName,
  ]);

  const removeDrawing = useCallback((id: string) => {
    setDrawings((prev) => {
      const drawing = prev.find((d) => d.id === id);
      if (drawing) {
        drawing.layer.remove();
      }
      return prev.filter((d) => d.id !== id);
    });
  }, []);

  const toggleDrawingVisibility = useCallback((id: string) => {
    setDrawings((prev) =>
      prev.map((drawing) => {
        if (drawing.id === id) {
          const newVisibility = !drawing.isVisible;
          const layer = drawing.layer as any;

          if (newVisibility) {
            if (featureGroupRef.current) {
              try {
                if (layer._map) {
                  drawing.layer.remove();
                }

                featureGroupRef.current.addLayer(drawing.layer);

                const fgMap = (featureGroupRef.current as any)._map;
                if (fgMap && !layer._map) {
                  drawing.layer.addTo(fgMap);
                }
              } catch (error) {
                console.error("Error adding layer back:", error);
              }
            }
          } else {
            if (layer._map) {
              drawing.layer.remove();
            }
          }

          return { ...drawing, isVisible: newVisibility };
        }
        return drawing;
      })
    );
  }, []);

  const updateDrawingName = useCallback((id: string, name: string) => {
    setDrawings((prev) =>
      prev.map((drawing) =>
        drawing.id === id ? { ...drawing, name } : drawing
      )
    );
  }, []);

  const addLayersToMap = useCallback(
    (featureGroup: L.FeatureGroup, mapInstance?: L.Map) => {
      featureGroupRef.current = featureGroup;
      const fgMap =
        mapInstance ?? (featureGroup as L.FeatureGroup & { _map?: L.Map })._map;

      drawings.forEach((drawing) => {
        if (!drawing.isVisible) return;
        try {
          const layer = drawing.layer as L.Layer & { _map?: L.Map };
          if (layer._map) return;
          featureGroup.addLayer(drawing.layer);
          if (fgMap) {
            drawing.layer.addTo(fgMap);
          }
        } catch (error) {
          console.error("[RulerContext] Failed to add layer:", error);
        }
      });
    },
    [drawings]
  );

  const downloadGeoJSON = useCallback(() => {
    const features = drawings.map((drawing) => {
      const layer = drawing.layer as any;
      let geometry: any = null;

      if (layer instanceof L.Marker || layer instanceof L.CircleMarker) {
        const latlng = layer.getLatLng();
        geometry = {
          type: "Point",
          coordinates: [latlng.lng, latlng.lat],
        };
      } else if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
        const latlngs = layer.getLatLngs() as L.LatLng[];
        geometry = {
          type: "LineString",
          coordinates: latlngs.map((ll) => [ll.lng, ll.lat]),
        };
      } else if (layer instanceof L.Polygon) {
        const latlngs = layer.getLatLngs()[0] as L.LatLng[];
        geometry = {
          type: "Polygon",
          coordinates: [latlngs.map((ll) => [ll.lng, ll.lat])],
        };
      }

      return {
        type: "Feature",
        properties: {
          name: drawing.name,
          type: drawing.type,
          result: drawing.result,
          visible: drawing.isVisible,
        },
        geometry,
      };
    });

    const geojson = {
      type: "FeatureCollection",
      features,
    };

    const blob = new Blob([JSON.stringify(geojson, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `drawings-${
      new Date().toISOString().split("T")[0]
    }.geojson`;
    link.click();
    URL.revokeObjectURL(url);
  }, [drawings]);

  return (
    <RulerModalContext.Provider
      value={{
        isModalOpen,
        activeFunction,
        measureResult,
        isFinished,
        isConfirmed,
        currentLayer,
        drawings,
        setDrawings,
        openModal,
        closeModal,
        activeFunctionHandler,
        setMeasure,
        cancelMeasure,
        confirmMeasure,
        removeDrawing,
        toggleDrawingVisibility,
        updateDrawingName,
        addLayersToMap,
        clearAllDrawings,
        downloadGeoJSON,
        profileData,
        isLoadingProfile,
        profileLayerName,
        profileLayerRef,
        profileGenRef,
        setProfileData,
        setIsLoadingProfile,
        setProfileLayerName,
        clearProfile,
      }}
    >
      {children}
    </RulerModalContext.Provider>
  );
};

export const useRulerModalContext = (): IRulerModalContext => {
  const context = useContext(RulerModalContext);
  if (context === undefined) {
    throw new Error("useModalContext must be used within a ModalProvider");
  }
  return context;
};
